import styled from "@emotion/styled";
import { Box, H2, Link, RichText, Stack } from "atoms";
import { GenericImage } from "features/images";
import { preventForwarding } from "styles/styleUtils";
import { OpenCallCardDate } from "./OpenCallCardDate";
import { OpenCallPartnerChip } from "./OpenCallPartnerChip";
import { OpenCallTagBadge } from "./OpenCallTagBadge";
import type { OpenCall } from "../types";
import { useMyOpenCallSubmissions } from "../api/useOpenCallSubmissions";
import { useUsersOpenCallVotes } from "../api/useUsersOpenCallVotes";
import { getOpenCallTag } from "../utils/getOpenCallTag";

type OpenCallCardProps = {
  openCall: OpenCall;
  isFeatured?: boolean;
  linkToVote?: boolean;
  hideOnMobile?: boolean;
};

function OpenCallCard({
  openCall,
  linkToVote = false,
  isFeatured,
  ...props
}: OpenCallCardProps) {
  const { data: mySubmissions } = useMyOpenCallSubmissions();
  const submitted = !!mySubmissions?.data?.find(
    (sub) => sub.openCallId === openCall.id
  );

  const { data: myVotes } = useUsersOpenCallVotes();
  const voted = !!myVotes?.openCallIds.includes(openCall.id);

  return (
    <ContentBox isFeatured={isFeatured} {...props}>
      <CardMain isFeatured={isFeatured}>
        <ContentImage isFeatured={isFeatured}>
          <SGenericImage
            src={openCall.heroImage}
            alt={openCall.heroImage.altText ?? ""}
            sizes="(max-width: 1000px) 300px, 600px"
            isFeatured={isFeatured}
          />

          {openCall.partnerName && (
            <SOpenCallPartnerChip
              partnerName={openCall.partnerName}
              partnerLogo={openCall.partnerLogo}
              hasBg
            />
          )}
        </ContentImage>

        <ContentTitle gap="xs">
          <Stack gap="xs">
            <OpenCallTagBadge
              openCall={openCall}
              submitted={submitted}
              voted={voted}
            />

            <H2 size="md">{openCall.title}</H2>
          </Stack>

          {isFeatured && (
            <FeaturedOpenCallBody
              openCall={openCall}
              isFeatured
              linkToVote={false}
            />
          )}
        </ContentTitle>

        {!isFeatured && (
          <OpenCallCardBody openCall={openCall} linkToVote={linkToVote} />
        )}
      </CardMain>
    </ContentBox>
  );
}

type WithIsFeatured = { isFeatured?: boolean; hideOnMobile?: boolean };

const ContentBox = styled(Box)<WithIsFeatured>(
  ({ isFeatured, hideOnMobile, theme }) => ({
    display: hideOnMobile ? "none" : "flex",
    [theme.breakpoints.tablet]: {
      display: "flex",
    },
    overflow: "hidden",
    padding: 0,
    position: "relative",
    textAlign: "left",
    "&&": {
      color: theme.colors.fg,
    },
    ...(isFeatured
      ? {
          marginBottom: "2rem",
          [theme.breakpoints.xs]: {
            marginBottom: 0,
          },
        }
      : {
          fontSize: theme.fontSizes.xs,
        }),
  })
);

const CardMain = styled.div<WithIsFeatured>(({ theme, isFeatured }) => ({
  flex: 1,
  flexDirection: "column",
  [theme.breakpoints.xs]: {
    display: "flex",
  },
  ...(isFeatured && {
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
  }),
}));

const ContentImage = styled.div<WithIsFeatured>(({ theme, isFeatured }) => ({
  position: "relative",

  ...(isFeatured
    ? {
        flex: "0 0 45%",
        [theme.breakpoints.xs]: {
          position: "relative",
          alignSelf: "stretch",
          img: {
            objectFit: "cover",
            width: "100%",
            height: "100%",
            display: "block",
          },
        },
        [theme.breakpoints.desktopMedium]: {
          flex: "0 0 50%",
        },
      }
    : {
        img: {
          aspectRatio: "1",
          display: "block",
        },
      }),
}));

const SGenericImage = styled(GenericImage, {
  shouldForwardProp: preventForwarding<WithIsFeatured>("isFeatured"),
})<WithIsFeatured>({
  height: "100%",
});

const ContentTitle = styled(Stack)(({ theme }) => ({
  padding: "1rem 1rem 0.5rem 1rem",
  [theme.breakpoints.maxXs]: {
    alignContent: "center",
    flex: "1",
  },
}));

const ContentBody = styled(RichText)(({ theme }) => ({
  lineHeight: 1.3,
  margin: 0,
  flex: "0 0 100%",
  [theme.breakpoints.xs]: {
    flex: 1,
  },
}));

const SOpenCallPartnerChip = styled(OpenCallPartnerChip)({
  position: "absolute",
  left: 10,
  bottom: 10,
  zIndex: 2,
});

type OpenCallBodyProps = {
  openCall: OpenCall;
  isFeatured?: boolean;
  linkToVote: boolean;
};

function OpenCallCardBody({
  openCall,
  isFeatured,
  linkToVote,
  ...props
}: OpenCallBodyProps) {
  const tag = getOpenCallTag(openCall);

  return (
    <Wrapper {...props} gap="xs">
      {openCall.subtitle && <ContentBody value={openCall.subtitle} />}

      <CardBodyRow isFeatured={isFeatured}>
        <Link
          variant="secondary"
          size="xxs"
          to={`/open-calls/${openCall.id}${
            linkToVote && tag === "Voting Open" ? "/vote" : ""
          }`}
        >
          {linkToVote && tag === "Voting Open" ? "Vote Now" : "View Details"}
        </Link>

        <OpenCallCardDate openCall={openCall} isFeatured={isFeatured} />
      </CardBodyRow>
    </Wrapper>
  );
}

const CardBodyRow = styled.div<{ isFeatured?: boolean }>(({ isFeatured }) => ({
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: isFeatured ? "flex-start" : "center",
  flexDirection: isFeatured ? "column" : "row",
}));
const FeaturedOpenCallBody = styled(OpenCallCardBody)({
  padding: 0,
});

const Wrapper = styled(Stack)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 5,
  padding: "0 1rem 1rem 1rem",
  a: {
    position: "static",
  },
  "a::after": {
    content: "''",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    position: "absolute",
    zIndex: 2,
  },
});

export { OpenCallCard };
